import {
  Component,
  Host,
  Prop,
  Element,
  State,
  h,
  Event,
  EventEmitter,
  Listen,
  getAssetPath
} from "@stencil/core";

import { getIconPath, MercuryBundles } from "@genexus/mercury";

import { Locale } from "../../../common/locale";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/edit",
  "components/accordion",
  "components/tabular-grid",
  "components/icon",
  "chameleon/scrollbar",
  "utils/layout",
  "utils/form",
  "utils/typography",
  "utils/spacing",
  "utils/elevation"
];
const ANGULAR_LOGO = getAssetPath(
  "./gx-ide-assets/launchpad-web-apps/images/angular-logo.svg"
);
const ACCORDION_RECENT_OBJECTS_ID = "recent-objects";

const COLUMN_NAME = "name";
const COLUMN_MODULE = "module";
const COLUMN_TYPE = "type";
const COLUMN_DESCRIPTION = "description";

@Component({
  tag: "gx-ide-launchpad-web-apps",
  styleUrl: "launchpad-web-apps.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/launchpad-web-apps"]
})
export class GxIdeLaunchpadWebApps {
  #componentLocale: any;

  @Element() el: HTMLGxIdeLaunchpadWebAppsElement;

  /**
   * Array of types of web objects organized by category
   */
  @Prop() readonly objects: ObjectType[] = [];

  /**
   * Callback function for object selection (passed from parent)
   */
  @Prop() onObjectSelected?: (event: any) => void;

  /**
   * Event emitted when copying to clipboard
   */
  @Event() copyToClipboard: EventEmitter<{ url: string }>;

  /**
   * Filter value for searching objects
   */
  @State() filterValue: string = "";

  /**
   * State to control if recent objects accordion is expanded
   */
  @State() recentObjectsExpanded: boolean = true;

  /**
   * Sorting order for recent objects table
   */
  @State() order: { orderCriteria: string | null; ascending: boolean | null } =
    {
      orderCriteria: null,
      ascending: null
    };

  async componentWillLoad() {
    try {
      this.#componentLocale = await Locale.getComponentStrings(this.el);
    } catch (error) {
      console.error("Error loading locale:", error);
      this.#componentLocale = {};
    }
  }

  @Listen("expandedChange")
  handleAccordionExpandedChange(e: CustomEvent) {
    if (e.detail.id === ACCORDION_RECENT_OBJECTS_ID) {
      this.recentObjectsExpanded = e.detail.expanded;
    }
  }

  /**
   * Filters objects based on search criteria
   */
  #filterObjects(object: WebAppObject): boolean {
    if (!this.filterValue) return true;

    const filterV = this.filterValue.toLowerCase();
    return (
      (object.module && object.module.toLowerCase().includes(filterV)) ||
      (object.name && object.name.toLowerCase().includes(filterV)) ||
      (object.type && object.type.toLowerCase().includes(filterV)) ||
      (object.description && object.description.toLowerCase().includes(filterV))
    );
  }

  /**
   * Sorts recent objects
   */
  #sortRecentObjects = (a: WebAppObject, b: WebAppObject): number => {
    if (this.order.orderCriteria) {
      const orderCriteriaA =
        this.#getObjectValue(a, this.order.orderCriteria)?.toUpperCase() || "";
      const orderCriteriaB =
        this.#getObjectValue(b, this.order.orderCriteria)?.toUpperCase() || "";
      if (orderCriteriaA < orderCriteriaB) {
        return this.order.ascending ? -1 : 1;
      }
      if (orderCriteriaA > orderCriteriaB) {
        return this.order.ascending ? 1 : -1;
      }
    }
    return 0;
  };

  /**
   * Helper function to safely get object values
   */
  #getObjectValue(obj: WebAppObject, key: string): string {
    switch (key) {
      case "name":
        return obj.name || "";
      case "module":
        return obj.module || "";
      case "type":
        return obj.type || "";
      case "description":
        return obj.description || "";
      default:
        return "";
    }
  }

  /**
   * Handles column sort changes
   */
  #handleColumnSortChanged = (e: CustomEvent) => {
    const { columnId, sortDirection } = e.detail;
    this.order = {
      orderCriteria: columnId,
      ascending: sortDirection === "asc"
    };
  };

  #getObjectTypeIcon(object: WebAppObject): string {
    const iconMap: Record<string, string> = {
      "36e32e2d-023e-4188-95df-d13573bac2e0:API": getIconPath({
        category: "objects",
        name: "api"
      }),
      "2a9e9aba-d2de-4801-ae7f-5e3819222daf:Data Provider": getIconPath({
        category: "objects",
        name: "data-provider"
      }),
      "9bdcc055-174e-4af6-96cb-a2ceef6c5f09:Menu": getIconPath({
        category: "objects",
        name: "dashboard"
      }),
      "9bdcc055-174e-4af6-96cb-a2ceef6c5f09:Dashboard": getIconPath({
        category: "objects",
        name: "dashboard"
      }),
      "0eee17f4-cef9-4988-aed4-c2da632be85b:Mini App": getIconPath({
        category: "objects",
        name: "mini-app"
      }),
      "d82625fd-5892-40b0-99c9-5c8559c197fc:Panel": getIconPath({
        category: "objects",
        name: "panel-for-sd"
      }),
      "d82625fd-5892-40b0-99c9-5c8559c197fc:SDPanel": getIconPath({
        category: "objects",
        name: "panel-for-sd"
      }),
      "84a12160-f59b-4ad7-a683-ea4481ac23e9:Procedure": getIconPath({
        category: "objects",
        name: "procedure"
      }),
      "a7e6cce5-1171-49e9-a6a0-07f137eedc07:Super App": getIconPath({
        category: "objects",
        name: "super-app"
      }),
      "1db606f2-af09-4cf9-a3b5-b481519d28f6:Transaction": getIconPath({
        category: "objects",
        name: "transaction"
      }),
      "c9584656-94b6-4ccd-890f-332d11fc2c25:Web Panel": getIconPath({
        category: "objects",
        name: "webpanel"
      }),
      "c9584656-94b6-4ccd-890f-332d11fc2c25:WebPanel": getIconPath({
        category: "objects",
        name: "webpanel"
      }),
      "c9584656-94b6-4ccd-890f-332d11fc2c25:Web Component": getIconPath({
        category: "objects",
        name: "web-component"
      }),
      "c9584656-94b6-4ccd-890f-332d11fc2c25:WebComponent": getIconPath({
        category: "objects",
        name: "web-component"
      }),
      "15cf49b5-fc38-4899-91b5-395d02d79889:Work With": getIconPath({
        category: "patterns",
        name: "workwith-for-sd"
      }),
      "15cf49b5-fc38-4899-91b5-395d02d79889:WorkWith": getIconPath({
        category: "patterns",
        name: "workwith-for-sd"
      }),
      "API": getIconPath({ category: "objects", name: "api" }),
      "Data Provider": getIconPath({
        category: "objects",
        name: "data-provider"
      }),
      "Menu": getIconPath({ category: "objects", name: "dashboard" }),
      "Dashboard": getIconPath({ category: "objects", name: "dashboard" }),
      "Mini App": getIconPath({ category: "objects", name: "mini-app" }),
      "Panel": getIconPath({ category: "objects", name: "panel-for-sd" }),
      "SDPanel": getIconPath({ category: "objects", name: "panel-for-sd" }),
      "Procedure": getIconPath({ category: "objects", name: "procedure" }),
      "Super App": getIconPath({ category: "objects", name: "super-app" }),
      "Transaction": getIconPath({ category: "objects", name: "transaction" }),
      "Web Panel": getIconPath({ category: "objects", name: "webpanel" }),
      "WebPanel": getIconPath({ category: "objects", name: "webpanel" }),
      "Web Component": getIconPath({
        category: "objects",
        name: "web-component"
      }),
      "WebComponent": getIconPath({
        category: "objects",
        name: "web-component"
      }),
      "Work With": getIconPath({
        category: "patterns",
        name: "workwith-for-sd"
      }),
      "WorkWith": getIconPath({ category: "patterns", name: "workwith-for-sd" })
    };

    if (object.key?.type) {
      const fullKey = `${object.key.type}:${object.type}`;
      if (iconMap[fullKey]) {
        return iconMap[fullKey];
      }
    }

    if (iconMap[object.type]) {
      return iconMap[object.type];
    }

    return getIconPath({ category: "objects", name: "object" });
  }

  /**
   * Handles object click and calls parent callback
   */
  #handleObjectClick(object: WebAppObject) {
    if (object.key && this.onObjectSelected) {
      // Send the full key object as string in format "type|id"
      const keyString = `${object.key.type}|${object.key.id}`;
      this.onObjectSelected({ detail: { key: keyString } });
    }
  }

  /**
   * Renders main objects section
   */
  #renderMainObjects() {
    const mainObjects =
      this.objects.find(obj => obj.type === "main")?.objects || [];
    const filteredObjects = mainObjects.filter(obj => this.#filterObjects(obj));

    if (mainObjects.length === 0) {
      return (
        <div class="no-results">
          <p class="body-regular-s">
            {this.#componentLocale?.noMainObjects ||
              "There are no main objects to display"}
          </p>
        </div>
      );
    }

    if (filteredObjects.length === 0) {
      return (
        <div class="no-results">
          <p class="body-regular-s">
            {this.#componentLocale?.noObjectsMatchSearch}
          </p>
        </div>
      );
    }

    return (
      <div class="main-objects-grid">
        {filteredObjects.map((object, index) => (
          <button
            key={index}
            class="object-card elevation-1"
            onClick={() => this.#handleObjectClick(object)}
          >
            <div class="card-header">
              <div class="left">
                <h1 class="name body-semi-bold-s">{object.name}</h1>
                <h3 class="module body-regular-xs">{object.module}</h3>
                <p class="type body-italic-xs">{object.type}</p>
              </div>
              <div class="right">
                <div class="type-container">
                  <ch-image
                    class="type-icon icon-m"
                    src={this.#getObjectTypeIcon(object)}
                  />
                </div>
              </div>
            </div>
            {(object.description || object.angular) && (
              <footer class="card-footer">
                <div class="left">
                  {object.description && (
                    <p class="description body-regular-xs">
                      {object.description}
                    </p>
                  )}
                </div>
                <div class="right">
                  {object.angular && (
                    <img src={ANGULAR_LOGO} alt="Angular Logotype" />
                  )}
                </div>
              </footer>
            )}
          </button>
        ))}
        {
          // WA: Add empty divs to ensure a minimum of 4 grid items for proper layout
          filteredObjects.length < 4 &&
            Array.from({ length: 4 - filteredObjects.length }).map(
              (_, index) => <div key={`empty-${index}`}></div>
            )
        }
      </div>
    );
  }

  /**
   * Renders recent objects section
   */
  #renderRecentObjects() {
    const recentObjects =
      this.objects.find(obj => obj.type === "recent")?.objects || [];
    const filteredObjects = recentObjects.filter(obj =>
      this.#filterObjects(obj)
    );

    if (recentObjects.length === 0) {
      return (
        <div class="no-objects">
          <p class="body-regular-s">{this.#componentLocale?.noRecentObjects}</p>
        </div>
      );
    }

    if (filteredObjects.length === 0) {
      return (
        <div class="no-objects">
          <p class="body-regular-s">
            {this.#componentLocale?.noObjectsMatchSearch}
          </p>
        </div>
      );
    }

    const sortedObjects = [...filteredObjects].sort(this.#sortRecentObjects);

    return (
      <div class="recent-objects-grid-container">
        <div class="tabular-grid-scroll-container">
          <ch-tabular-grid class="tabular-grid elevation-2">
            <ch-tabular-grid-columnset class="tabular-grid-column-set">
              <ch-tabular-grid-column
                class="tabular-grid-column"
                columnId={COLUMN_NAME}
                columnName={this.#componentLocale?.columns?.name || "Name"}
                columnNameHidden={false}
                settingable={false}
                sortable
                onColumnSortChanged={this.#handleColumnSortChanged}
                size="25%"
                resizable={false}
              ></ch-tabular-grid-column>
              <ch-tabular-grid-column
                class="tabular-grid-column"
                columnId={COLUMN_MODULE}
                columnName={this.#componentLocale?.columns?.module || "Module"}
                columnNameHidden={false}
                settingable={false}
                sortable
                onColumnSortChanged={this.#handleColumnSortChanged}
                size="20%"
                resizable={false}
              ></ch-tabular-grid-column>
              <ch-tabular-grid-column
                class="tabular-grid-column"
                columnId={COLUMN_TYPE}
                columnName={this.#componentLocale?.columns?.type || "Type"}
                columnNameHidden={false}
                settingable={false}
                sortable
                onColumnSortChanged={this.#handleColumnSortChanged}
                size="20%"
                resizable={false}
              ></ch-tabular-grid-column>
              <ch-tabular-grid-column
                class="tabular-grid-column"
                columnId={COLUMN_DESCRIPTION}
                columnName={
                  this.#componentLocale?.columns?.description || "Description"
                }
                columnNameHidden={false}
                settingable={false}
                sortable={false}
                size="35%"
                resizable={false}
              ></ch-tabular-grid-column>
            </ch-tabular-grid-columnset>

            <ch-tabular-grid-rowset class="tabular-grid-rowset">
              {sortedObjects.map((obj, index) => (
                <ch-tabular-grid-row
                  rowid={obj.key?.id || index.toString()}
                  class="tabular-grid-row"
                  onClick={() => this.#handleObjectClick(obj)}
                >
                  <ch-tabular-grid-cell class="tabular-grid-cell">
                    <div class="cell-content">
                      <ch-image
                        class="object-icon icon-m"
                        src={this.#getObjectTypeIcon(obj)}
                      />
                      <span class="object-name">{obj.name || "Unnamed"}</span>
                    </div>
                  </ch-tabular-grid-cell>
                  <ch-tabular-grid-cell class="tabular-grid-cell">
                    {obj.module || ""}
                  </ch-tabular-grid-cell>
                  <ch-tabular-grid-cell class="tabular-grid-cell">
                    {obj.type}
                  </ch-tabular-grid-cell>
                  <ch-tabular-grid-cell class="tabular-grid-cell">
                    {obj.description || ""}
                  </ch-tabular-grid-cell>
                </ch-tabular-grid-row>
              ))}
            </ch-tabular-grid-rowset>
          </ch-tabular-grid>
        </div>
      </div>
    );
  }

  render() {
    if (!this.objects || this.objects.length === 0) {
      return (
        <Host>
          <ch-theme model={CSS_BUNDLES}></ch-theme>
          <div class="no-data">
            <p class="body-regular-s">
              {this.#componentLocale?.noData ||
                "No web applications data available"}
            </p>
          </div>
        </Host>
      );
    }

    return (
      <Host>
        <ch-theme model={CSS_BUNDLES}></ch-theme>

        <header class="header dialog-header">
          <div class="field field-block">
            <ch-edit
              class="input input-filter"
              id="filter"
              placeholder={this.#componentLocale?.search || "Search"}
              value={this.filterValue}
              accessibleName={this.#componentLocale?.search || "Search"}
              onInput={e => (this.filterValue = String(e.detail))}
            />
          </div>
        </header>

        <div class="main">
          <ch-accordion-render
            class="accordion-filled-header elevation-1 web-apps-accordion"
            model={[
              {
                id: "main-objects",
                caption: "Main Objects",
                expanded: true
              },
              {
                id: ACCORDION_RECENT_OBJECTS_ID,
                caption: "Recent Objects",
                expanded: this.recentObjectsExpanded
              }
            ]}
          >
            <div slot="main-objects" class="accordion-content spacing-body">
              {this.#renderMainObjects()}
            </div>
            <div
              slot={ACCORDION_RECENT_OBJECTS_ID}
              class="accordion-content spacing-body"
            >
              {this.#renderRecentObjects()}
            </div>
          </ch-accordion-render>
        </div>
      </Host>
    );
  }
}

interface WebAppObject {
  name: string;
  module: string;
  type: string;
  description?: string;
  url?: string;
  key?: {
    type: string;
    id: string;
  };
  angular?: boolean;
}

interface ObjectType {
  type: "main" | "recent";
  objects: WebAppObject[];
}
